import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, ButtonGroup, Form, Spinner, Pagination, Placeholder, Collapse } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { getAllOrders, getAllAccounts, getAccountTickers } from '../services/api';
import { formatDateTime, formatDate, formatISODate, parseDateForSort } from '../utils/dateUtils';

const getDefaultTodayRange = () => {
  const today = formatISODate(new Date());
  return { startDate: today, endDate: today };
};

// QuickBuy (useQuickBuy.js) repurposes the 'STOP_LOSS' order type to represent a
// "buy stop" -- a BUY order that triggers on a price breakout upward -- since the
// order model only has LIMIT/STOP_LOSS as type values. Displaying that as plain
// "Stop loss" is misleading: the term conventionally means a protective SELL
// order, the opposite direction of what a buy-side breakout order does. Label by
// action so the row reads correctly either way; the underlying type value (and
// the filter dropdown, which filters on that raw value) is unchanged.
const formatOrderType = (type, action) => {
  if (type === 'LIMIT') return 'Limit';
  return String(action).toUpperCase() === 'BUY' ? 'Buy Stop' : 'Stop Loss';
};

// Projected gain/loss if a SELL order fills at its listed price, against the
// account's current cost basis for that symbol (holdingsMap, populated by
// fetchHoldingsForOrders). Returns null when it can't be computed (a BUY has
// no existing position to compare against yet; a SELL might reference a
// symbol/account this hasn't loaded holdings for) -- callers render '-' for null.
const computeOrderGainLoss = (order, holdingsMap) => {
  if (String(order.action).toUpperCase() !== 'SELL') return null;
  const costBasis = holdingsMap[order.account_id]?.[order.symbol];
  if (costBasis === null || costBasis === undefined) return null;
  const price = Number(order.price);
  const quantity = Number(order.quantity);
  if (!Number.isFinite(price) || !Number.isFinite(quantity)) return null;
  return (price - Number(costBasis)) * quantity;
};

const getDefaultFilters = () => {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(endDate.getDate() - 7);
  return {
    account_id: '',
    symbol: '',
    status: '',
    type: '',
    start_date: formatISODate(startDate),
    end_date: formatISODate(endDate),
    timeframe: '1week'
  };
};

function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(() => {
    try {
      const stored = localStorage.getItem('ordersFilters');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && typeof parsed === 'object') {
          return { ...getDefaultFilters(), ...parsed };
        }
      }
    } catch {
      // Ignore malformed storage
    }
    return getDefaultFilters();
  });
  const [accountsMap, setAccountsMap] = useState({});
  // { [account_id]: { [symbol]: cost_basis } } -- holdings context for the
  // Gain/Loss column, fetched per unique account referenced by the current
  // page of orders (see fetchHoldingsForOrders below).
  const [holdingsMap, setHoldingsMap] = useState({});
  const [sortColumn, setSortColumn] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [sortedOrders, setSortedOrders] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [showFilters, setShowFilters] = useState(true);
  const filtersStorageKey = 'ordersFilters';

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const normalizedFilters = { ...filters };
      if (normalizedFilters.start_date && normalizedFilters.start_date.length === 10) {
        normalizedFilters.start_date = `${normalizedFilters.start_date}T00:00:00`;
      }
      if (normalizedFilters.end_date && normalizedFilters.end_date.length === 10) {
        normalizedFilters.end_date = `${normalizedFilters.end_date}T23:59:59.999`;
      }
      const response = await getAllOrders(currentPage, pageSize, normalizedFilters);
      setOrders(response.data.orders);
      setTotalPages(response.data.pagination.total_pages);
      setTotalOrders(response.data.pagination.total_orders);
    } catch (err) {
      setOrders([]);
      setTotalPages(1);
      setTotalOrders(0);
    } finally {
      setLoading(false);
    }
  };

  // Sort orders when orders or sort configuration changes
  useEffect(() => {
    if (orders && orders.length > 0) {
      const sorted = [...orders].sort((a, b) => {
        let aValue, bValue;
        
        switch (sortColumn) {
          case 'account':
            aValue = (accountsMap[a.account_id]?.name || a.account_id).toLowerCase();
            bValue = (accountsMap[b.account_id]?.name || b.account_id).toLowerCase();
            break;
          case 'symbol':
            aValue = a.symbol?.toLowerCase() || '';
            bValue = b.symbol?.toLowerCase() || '';
            break;
          case 'category':
            aValue = a.category?.toLowerCase() || '';
            bValue = b.category?.toLowerCase() || '';
            break;
          case 'quantity':
            aValue = Number(a.quantity) || 0;
            bValue = Number(b.quantity) || 0;
            break;
          case 'action':
            aValue = a.action?.toLowerCase() || '';
            bValue = b.action?.toLowerCase() || '';
            break;
          case 'type':
            aValue = a.type?.toLowerCase() || '';
            bValue = b.type?.toLowerCase() || '';
            break;
          case 'price':
            aValue = Number(a.price) || 0;
            bValue = Number(b.price) || 0;
            break;
          case 'status':
            aValue = a.status?.toLowerCase() || '';
            bValue = b.status?.toLowerCase() || '';
            break;
          case 'total':
            aValue = (Number(a.price) * Number(a.quantity)) || 0;
            bValue = (Number(b.price) * Number(b.quantity)) || 0;
            break;
          case 'size':
            aValue = Number(a.size) || 0;
            bValue = Number(b.size) || 0;
            break;
          case 'order_date':
          aValue = parseDateForSort(a.order_date);
          bValue = parseDateForSort(b.order_date);
          break;
        case 'created_at':
        default:
          aValue = parseDateForSort(a.created_at);
          bValue = parseDateForSort(b.created_at);
          break;
        }
        
        if (aValue < bValue) {
          return sortDirection === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortDirection === 'asc' ? 1 : -1;
        }
        return 0;
      });
      
      setSortedOrders(sorted);
    } else {
      setSortedOrders([]);
    }
  }, [orders, sortColumn, sortDirection, accountsMap]);
  
  // Handle table header click for sorting
  const handleHeaderClick = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };
  
  // Render sort indicator for table headers
  const renderSortIndicator = (column) => {
    if (sortColumn !== column) {
      return '';
    }
    return (
      <span style={{ marginLeft: '4px', fontSize: '0.85em' }}>
        {sortDirection === 'asc' ? '▲' : '▼'}
      </span>
    );
  };

  // Render pagination controls
  const renderPagination = () => {
    const items = [];
    const maxItems = 7; // Maximum number of page items to show
    
    // Add first page
    items.push(
      <Pagination.Item 
        key={1} 
        active={currentPage === 1}
        onClick={() => handlePageChange(1)}
      >
        1
      </Pagination.Item>
    );
    
    // Calculate start and end pages
    let startPage = Math.max(2, currentPage - 2);
    let endPage = Math.min(totalPages - 1, currentPage + 2);
    
    // Adjust for edge cases
    if (currentPage <= 3) {
      endPage = Math.min(totalPages - 1, maxItems - 2);
    } else if (currentPage >= totalPages - 2) {
      startPage = Math.max(2, totalPages - maxItems + 2);
    }
    
    // Add ellipsis after first page if needed
    if (startPage > 2) {
      items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
    }
    
    // Add intermediate pages
    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item 
          key={i} 
          active={currentPage === i}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>
      );
    }
    
    // Add ellipsis before last page if needed
    if (endPage < totalPages - 1) {
      items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
    }
    
    // Add last page if there are more than 1 page
    if (totalPages > 1) {
      items.push(
        <Pagination.Item 
          key={totalPages} 
          active={currentPage === totalPages}
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </Pagination.Item>
      );
    }
    
    return (
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center">
          <span className="me-2">Rows per page:</span>
          <Form.Select 
            size="sm" 
            value={pageSize} 
            onChange={handlePageSizeChange}
            style={{ width: '80px' }}
          >
            <option value="10">10</option>
            <option value="15">15</option>
            <option value="20">20</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </Form.Select>
          <span className="ms-3">
            {totalOrders > 0 ? (
              <>
                Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, totalOrders)} of {totalOrders} orders
              </>
            ) : (
              'No orders found'
            )}
          </span>
        </div>
        
        <Pagination size="sm">
          <Pagination.First 
            onClick={() => handlePageChange(1)} 
            disabled={currentPage === 1}
          />
          <Pagination.Prev 
            onClick={() => handlePageChange(Math.max(1, currentPage - 1))} 
            disabled={currentPage === 1}
          />
          
          {items}
          
          <Pagination.Next 
            onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))} 
            disabled={currentPage === totalPages}
          />
          <Pagination.Last 
            onClick={() => handlePageChange(totalPages)} 
            disabled={currentPage === totalPages}
          />
        </Pagination>
      </div>
    );
  };
  
  // Render pagination placeholder when loading
  const renderPaginationPlaceholder = () => {
    return (
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center">
          <Placeholder as="span" animation="glow" className="me-2">
            <Placeholder xs={6} />
          </Placeholder>
          <Placeholder as={Form.Select} animation="glow" style={{ width: '80px' }} />
          <Placeholder as="span" animation="glow" className="ms-3">
            <Placeholder xs={8} />
          </Placeholder>
        </div>
        <Placeholder.Button variant="primary" xs={5} />
      </div>
    );
  };

  const fetchAccounts = async () => {
    try {
      const response = await getAllAccounts();
      // Build a map of account_id to account name
      const map = {};
      (response.data || []).forEach(acc => {
        map[acc.account_id] = acc; // Store the full account object
      });
      setAccountsMap(map);
    } catch (err) {
      setAccountsMap({});
    }
  };

  // Gain/Loss only applies to SELL orders (a BUY has no existing position to
  // compare against yet), so only fetch holdings for accounts that actually
  // have a SELL order on the current page, one request per unique account.
  const fetchHoldingsForOrders = async (ordersToFetch) => {
    const accountIds = Array.from(new Set(
      (ordersToFetch || [])
        .filter(order => String(order.action).toUpperCase() === 'SELL')
        .map(order => order.account_id)
    ));

    if (accountIds.length === 0) {
      setHoldingsMap({});
      return;
    }

    try {
      const responses = await Promise.all(
        accountIds.map(accountId =>
          getAccountTickers(accountId)
            .then(res => ({ accountId, tickers: res.data || [] }))
            .catch(() => ({ accountId, tickers: [] }))
        )
      );

      const map = {};
      responses.forEach(({ accountId, tickers }) => {
        map[accountId] = {};
        tickers.forEach(ticker => {
          if (ticker.cost_basis !== null && ticker.cost_basis !== undefined) {
            map[accountId][ticker.symbol] = ticker.cost_basis;
          }
        });
      });
      setHoldingsMap(map);
    } catch (err) {
      setHoldingsMap({});
    }
  };

  useEffect(() => {
    fetchOrders();
    fetchAccounts();
    // eslint-disable-next-line
  }, [currentPage, pageSize]);

  // Refresh holdings context whenever the visible set of orders changes.
  useEffect(() => {
    fetchHoldingsForOrders(orders);
    // eslint-disable-next-line
  }, [orders]);

  // Refetch orders when filters change
  useEffect(() => {
    setCurrentPage(1); // Reset to first page when filters change
    fetchOrders();
    // eslint-disable-next-line
  }, [filters]);

  // Persist filters to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(filtersStorageKey, JSON.stringify(filters));
    } catch {
      // Ignore storage errors
    }
  }, [filters]);

  useEffect(() => {
    if (filters.timeframe === 'today') {
      const { startDate, endDate } = getDefaultTodayRange();
      if (filters.start_date !== startDate || filters.end_date !== endDate) {
        setFilters(prev => ({
          ...prev,
          start_date: startDate,
          end_date: endDate
        }));
      }
    }
  }, [filters.timeframe, filters.start_date, filters.end_date]);

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleTimeframeChange = (e) => {
    const { value } = e.target;
    const endDate = new Date();
    let startDate = new Date();

    switch (value) {
      case 'today':
        startDate = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 0, 0, 0);
        break;
      case '2days':
        startDate.setDate(endDate.getDate() - 2);
        break;
      case '3days':
        startDate.setDate(endDate.getDate() - 3);
        break;
      case '1week':
        startDate.setDate(endDate.getDate() - 7);
        break;
      case '2weeks':
        startDate.setDate(endDate.getDate() - 14);
        break;
      case '1month':
        startDate.setMonth(endDate.getMonth() - 1);
        break;
      case '2months':
        startDate.setMonth(endDate.getMonth() - 2);
        break;
      case '6months':
        startDate.setMonth(endDate.getMonth() - 6);
        break;
      case 'custom':
        setFilters(prev => ({ ...prev, timeframe: value }));
        return;
      default:
        startDate.setDate(endDate.getDate() - 7);
    }

    setFilters(prev => ({
      ...prev,
      timeframe: value,
      start_date: formatISODate(startDate),
      end_date: formatISODate(endDate)
    }));

    setCurrentPage(1);
  };

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };
  
  const handlePageSizeChange = (e) => {
    setPageSize(parseInt(e.target.value));
    setCurrentPage(1); // Reset to first page
  };

  // Copy the customStyles from AccountDetails.js
  const customStyles = `
    /* Enhanced card styling for maximum contrast and prominence */
    .account-details-card {
        background-color: var(--card-bg) !important;
        border: 2px solid var(--border-color) !important;
        border-radius: 8px !important;
        box-shadow: 0 4px 16px var(--shadow-color) !important;
        transition: all 0.3s ease-in-out !important;
    }
    .account-details-card:hover {
        transform: translateY(-2px) !important;
        box-shadow: 0 8px 24px var(--shadow-color) !important;
    }
    [data-theme="light"] .account-details-card {
        background-color: #ffffff !important;
        border-color: #d1d5db !important;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1) !important;
    }
    [data-theme="light"] .account-details-card:hover {
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15) !important;
    }
    [data-theme="dark"] .account-details-card {
        background-color: #1f2937 !important;
        border-color: #4b5563 !important;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3) !important;
    }
    [data-theme="dark"] .account-details-card:hover {
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4) !important;
    }
    /* Enhanced table container styling */
    .orders-table-container {
        background-color: var(--card-bg) !important;
        border: 2px solid var(--border-color) !important;
        border-radius: 8px !important;
        box-shadow: 0 4px 16px var(--shadow-color) !important;
        overflow: hidden !important;
        margin-bottom: 0 !important;
    }
    [data-theme="light"] .orders-table-container {
        background-color: #ffffff !important;
        border-color: #d1d5db !important;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1) !important;
    }
    [data-theme="dark"] .orders-table-container {
        background-color: #1f2937 !important;
        border-color: #4b5563 !important;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3) !important;
    }
    /* Enhanced table styling */
    .account-tickers-table {
        background-color: var(--card-bg) !important;
        border: none !important;
        margin-bottom: 0 !important;
    }
    /* Remove gap between last row and bottom of table container */
    .account-tickers-table-container .table {
        margin-bottom: 0 !important;
    }
    .account-tickers-table-container .ticker-table {
        margin-bottom: 0 !important;
    }
    /* Enhanced table header styling - override TickerTable.css */
    .ticker-table.account-tickers-table thead th,
    .account-tickers-table thead th,
    .account-tickers-table-header {
        background-color: var(--bg-secondary) !important;
        border-bottom: 2px solid var(--border-color) !important;
        font-weight: 600 !important;
        color: var(--text-primary) !important;
        padding: 6px 6px !important;
        transition: background-color 0.2s ease-in-out;
    }
    .ticker-table.account-tickers-table thead th:hover,
    .account-tickers-table thead th:hover,
    .account-tickers-table-header:hover {
        background-color: var(--hover-bg) !important;
        cursor: pointer;
    }
    [data-theme="light"] .ticker-table.account-tickers-table thead th,
    [data-theme="light"] .account-tickers-table thead th,
    [data-theme="light"] .account-tickers-table-header {
        background-color: #f8f9fa !important;
        border-bottom: 2px solid #d1d5db !important;
        color: #374151 !important;
    }
    [data-theme="light"] .ticker-table.account-tickers-table thead th:hover,
    [data-theme="light"] .account-tickers-table thead th:hover,
    [data-theme="light"] .account-tickers-table-header:hover {
        background-color: #e9ecef !important;
    }
    [data-theme="dark"] .ticker-table.account-tickers-table thead th,
    [data-theme="dark"] .account-tickers-table thead th,
    [data-theme="dark"] .account-tickers-table-header {
        background-color: #374151 !important;
        border-bottom: 2px solid #4b5563 !important;
        color: #f9fafb !important;
    }
    [data-theme="dark"] .ticker-table.account-tickers-table thead th:hover,
    [data-theme="dark"] .account-tickers-table thead th:hover,
    [data-theme="dark"] .account-tickers-table-header:hover {
        background-color: #4b5563 !important;
    }
    /* Enhanced table row styling - more specific selectors to override TickerTable styles */
    .account-tickers-table-container .ticker-table.tickers-table tbody tr,
    .account-tickers-table.ticker-table.tickers-table tbody tr {
        border-bottom: 1px solid var(--border-color) !important;
        transition: all 0.3s ease-in-out !important;
        font-size: 0.95rem !important;
    }
    .account-tickers-table-container .ticker-table.tickers-table tbody tr:hover {}
    [data-theme="light"] .account-tickers-table-container .ticker-table.tickers-table tbody tr:hover {}
    [data-theme="dark"] .account-tickers-table-container .ticker-table.tickers-table tbody tr:hover {}
    /* Enhanced table cell styling - more specific selectors to override TickerTable styles */
    .account-tickers-table-container .ticker-table.tickers-table tbody td,
    .account-tickers-table.ticker-table.tickers-table tbody td {
        padding: 4px 6px !important;
        border-bottom: 1px solid var(--border-color) !important;
        vertical-align: middle !important;
        transition: all 0.3s ease-in-out !important;
    }
    .account-tickers-table-container .ticker-table.tickers-table tbody tr:hover td,
    .account-tickers-table.ticker-table.tickers-table tbody tr:hover td {
        color: var(--text-primary) !important;
        font-weight: 500 !important;
        transform: scale(1.02) !important;
    }
    [data-theme="light"] .account-tickers-table-container .ticker-table.tickers-table tbody td,
    [data-theme="light"] .account-tickers-table.ticker-table.tickers-table tbody td {
        border-bottom: 1px solid #e5e7eb !important;
    }
    [data-theme="light"] .account-tickers-table-container .ticker-table.tickers-table tbody tr:hover td,
    [data-theme="light"] .account-tickers-table.ticker-table.tickers-table tbody tr:hover td {
        color: #1e40af !important;
    }
    [data-theme="dark"] .account-tickers-table-container .ticker-table.tickers-table tbody td,
    [data-theme="dark"] .account-tickers-table.ticker-table.tickers-table tbody td {
        border-bottom: 1px solid #374151 !important;
    }
    [data-theme="dark"] .account-tickers-table-container .ticker-table.tickers-table tbody tr:hover td,
    [data-theme="dark"] .account-tickers-table.ticker-table.tickers-table tbody tr:hover td {
        color: #93c5fd !important;
    }
    /* CSS Variables for theme support */
    :root {
        --card-bg: #ffffff;
        --border-color: #d1d5db;
        --shadow-color: rgba(0, 0, 0, 0.1);
        --text-primary: #374151;
        --bg-secondary: #f8f9fa;
        --hover-bg: #f1f5f9;
    }
    [data-theme="dark"] {
        --card-bg: #1f2937;
        --border-color: #4b5563;
        --shadow-color: rgba(0, 0, 0, 0.3);
        --text-primary: #f9fafb;
        --bg-secondary: #374151;
        --hover-bg: #4b5563;
    }
 

    /* Orders table hover effects - similar to tickers table */
    .orders-table tbody tr {
      border-bottom: 1px solid var(--border-color) !important;
      transition: all 0.3s ease-in-out !important;
      font-size: 0.95rem !important;
    }
    .orders-table tbody tr:hover {}
    [data-theme="light"] .orders-table tbody tr:hover {}
    [data-theme="dark"] .orders-table tbody tr:hover {}
    .orders-table tbody td {
      padding: 4px 6px !important;
      border-bottom: 1px solid var(--border-color) !important;
      vertical-align: middle !important;
      transition: all 0.3s ease-in-out !important;
    }
    .orders-table tbody tr:hover td {
      color: var(--text-primary) !important;
      font-weight: 500 !important;
      transform: scale(1.02) !important;
    }
    [data-theme="light"] .orders-table tbody td {
      border-bottom: 1px solid #e5e7eb !important;
    }
    [data-theme="light"] .orders-table tbody tr:hover td {
      color: #1e40af !important;
    }
    [data-theme="dark"] .orders-table tbody td {
      border-bottom: 1px solid #374151 !important;
    }
    [data-theme="dark"] .orders-table tbody tr:hover td {
      color: #93c5fd !important;
    }
    
    /* Orders filter card styling */
    .orders-card {
        background-color: var(--card-bg) !important;
        border: 2px solid var(--border-color) !important;
        border-radius: 8px !important;
        box-shadow: 0 4px 16px var(--shadow-color) !important;
        transition: all 0.3s ease-in-out !important;
    }
    .orders-card:hover {
        transform: translateY(-2px) !important;
        box-shadow: 0 8px 24px var(--shadow-color) !important;
    }
    [data-theme="light"] .orders-card {
        background-color: #ffffff !important;
        border-color: #d1d5db !important;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1) !important;
    }
    [data-theme="light"] .orders-card:hover {
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15) !important;
    }
    [data-theme="dark"] .orders-card {
        background-color: #1f2937 !important;
        border-color: #4b5563 !important;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3) !important;
    }
    [data-theme="dark"] .orders-card:hover {
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4) !important;
    }
`;

  return (
    <Container className="mt-3">
      <style>{customStyles}</style>
      
      {/* Filter and Columns buttons */}
      <div className="d-flex justify-content-end mb-2">
        <Button
          variant="outline-primary"
          onClick={() => setShowFilters(!showFilters)}
          size="sm"
          className="text-nowrap me-2"
        >
          <i className={`bi bi-funnel${showFilters ? '-fill' : ''} me-1`}></i>
          Filters
        </Button>
      </div>
      
      {/* Filters Section */}
      <Collapse in={showFilters}>
        <Card className="mb-4 orders-card">
          <Card.Body className="p-2">
            <Form>
              <Row className="g-2">
                <Col md={2}>
                  <Form.Group className="mb-0">
                    <Form.Label className="mb-0 small">Symbol</Form.Label>
                    <Form.Control 
                      type="text"
                      name="symbol"
                      value={filters.symbol}
                      onChange={handleFilterChange}
                      placeholder="Enter symbol"
                      size="sm"
                    />
                  </Form.Group>
                </Col>
                
                <Col md={2}>
                  <Form.Group className="mb-0">
                    <Form.Label className="mb-0 small">Account</Form.Label>
                    <Form.Select
                      name="account_id"
                      value={filters.account_id}
                      onChange={handleFilterChange}
                      size="sm"
                    >
                      <option value="">All Accounts</option>
                      {Object.values(accountsMap).map(account => (
                        <option key={account.account_id} value={account.account_id}>
                          {account.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                
                <Col md={2}>
                  <Form.Group className="mb-0">
                    <Form.Label className="mb-0 small">Type</Form.Label>
                    <Form.Select
                      name="type"
                      value={filters.type}
                      onChange={handleFilterChange}
                      size="sm"
                    >
                      <option value="">All Types</option>
                      <option value="LIMIT">Limit</option>
                      <option value="STOP_LOSS">Stop Loss</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                
                <Col md={2}>
                  <Form.Group className="mb-0">
                    <Form.Label className="mb-0 small">Timeframe</Form.Label>
                    <Form.Select
                      name="timeframe"
                      value={filters.timeframe}
                      onChange={handleTimeframeChange}
                      size="sm"
                    >
                      <option value="today">Today</option>
                      <option value="2days">2 Days</option>
                      <option value="3days">3 Days</option>
                      <option value="1week">1 Week</option>
                      <option value="2weeks">2 Weeks</option>
                      <option value="1month">1 Month</option>
                      <option value="2months">2 Months</option>
                      <option value="6months">6 Months</option>
                      <option value="custom">Custom</option>
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={2} className={filters.timeframe === 'custom' ? '' : 'd-none'}>
                  <Form.Group className="mb-0">
                    <Form.Label className="mb-0 small">Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="start_date"
                      value={filters.start_date}
                      onChange={handleFilterChange}
                      size="sm"
                    />
                  </Form.Group>
                </Col>
                
                <Col md={2} className={filters.timeframe === 'custom' ? '' : 'd-none'}>
                  <Form.Group className="mb-0">
                    <Form.Label className="mb-0 small">End Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="end_date"
                      value={filters.end_date}
                      onChange={handleFilterChange}
                      size="sm"
                    />
                  </Form.Group>
                </Col>

                <Col md={2}>
                  <Form.Group className="mb-0">
                    <Form.Label className="mb-0 small">Status</Form.Label>
                    <ButtonGroup size="sm" className="w-100">
                      <Button
                        variant={filters.status === '' ? 'primary' : 'outline-primary'}
                        onClick={() => setFilters({ ...filters, status: '' })}
                      >
                        All
                      </Button>
                      <Button
                        variant={filters.status === 'PENDING' ? 'primary' : 'outline-primary'}
                        onClick={() => setFilters({ ...filters, status: 'PENDING' })}
                      >
                        Pending
                      </Button>
                      <Button
                        variant={filters.status === 'COMMITTED' ? 'primary' : 'outline-primary'}
                        onClick={() => setFilters({ ...filters, status: 'COMMITTED' })}
                      >
                        Committed
                      </Button>
                      <Button
                        variant={filters.status === 'FULFILLED' ? 'primary' : 'outline-primary'}
                        onClick={() => setFilters({ ...filters, status: 'FULFILLED' })}
                      >
                        Fulfilled
                      </Button>
                    </ButtonGroup>
                  </Form.Group>
                </Col>
                <Col md="auto" className="d-flex align-items-end ms-auto">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setFilters(getDefaultFilters())}
                  >
                    Reset
                  </Button>
                </Col>
              </Row>
            </Form>
          </Card.Body>
        </Card>
      </Collapse>
      
      <div className="orders-table-container mt-3">
        <div className="table-responsive">
        {loading ? (
          <div className="text-center my-4"><Spinner animation="border" /></div>
        ) : (
          <table className="table table-sm table-bordered mb-0 orders-table">
            <thead>
              <tr>
                <th 
                  style={{ cursor: 'pointer' }} 
                  onClick={() => handleHeaderClick('account')}
                  className="user-select-none align-middle"
                >
                  Account {renderSortIndicator('account')}
                </th>
                <th 
                  style={{ cursor: 'pointer' }} 
                  onClick={() => handleHeaderClick('symbol')}
                  className="user-select-none align-middle"
                >
                  Symbol {renderSortIndicator('symbol')}
                </th>
                <th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleHeaderClick('category')}
                  className="user-select-none align-middle"
                >
                  Category {renderSortIndicator('category')}
                </th>
                <th 
                  style={{ cursor: 'pointer' }} 
                  onClick={() => handleHeaderClick('action')}
                  className="user-select-none align-middle"
                >
                  Action {renderSortIndicator('action')}
                </th>
                <th 
                  style={{ cursor: 'pointer' }} 
                  onClick={() => handleHeaderClick('type')}
                  className="user-select-none align-middle"
                >
                  Type {renderSortIndicator('type')}
                </th>
                <th 
                  style={{ cursor: 'pointer' }} 
                  onClick={() => handleHeaderClick('quantity')}
                  className="user-select-none text-end"
                >
                  Quantity {renderSortIndicator('quantity')}
                </th>
                <th 
                  style={{ cursor: 'pointer' }} 
                  onClick={() => handleHeaderClick('price')}
                  className="user-select-none text-end"
                >
                  Price {renderSortIndicator('price')}
                </th>
                <th 
                  style={{ cursor: 'pointer' }} 
                  onClick={() => handleHeaderClick('total')}
                  className="user-select-none text-end"
                >
                  Total {renderSortIndicator('total')}
                </th>
                <th className="user-select-none text-end">Gain/Loss</th>
                <th 
                  style={{ cursor: 'pointer' }} 
                  onClick={() => handleHeaderClick('size')}
                  className="user-select-none text-end"
                >
                  Size {renderSortIndicator('size')}
                </th>
                <th 
                  style={{ cursor: 'pointer' }} 
                  onClick={() => handleHeaderClick('created_at')}
                  className="user-select-none align-middle"
                >
                  Created {renderSortIndicator('created_at')}
                </th>
                <th 
                  style={{ cursor: 'pointer' }} 
                  onClick={() => handleHeaderClick('order_date')}
                  className="user-select-none align-middle"
                >
                  Order Date {renderSortIndicator('order_date')}
                </th>
                <th 
                  style={{ cursor: 'pointer' }} 
                  onClick={() => handleHeaderClick('status')}
                  className="user-select-none align-middle"
                >
                  Status {renderSortIndicator('status')}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedOrders.length === 0 ? (
                <tr><td colSpan={13} className="text-center text-muted">No orders found.</td></tr>
              ) : sortedOrders.map(order => (
                <tr key={order.order_id}>
                  <td className="align-middle">
                    <Link 
                      to={`/account/${order.account_id}`}
                      className="text-decoration-none text-primary"
                      title="Go to Account Details"
                    >
                      {accountsMap[order.account_id]?.name || order.account_id}
                    </Link>
                  </td>
                  <td className="align-middle">{order.symbol}</td>
                  <td className="align-middle">{order.category || '-'}</td>
                  <td className="align-middle">{order.action}</td>
                  <td className="align-middle">{formatOrderType(order.type, order.action)}</td>
                  <td className="text-end align-middle">{order.quantity}</td>
                  <td className="text-end align-middle">${Number(order.price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td className="text-end align-middle">${(Number(order.price) * Number(order.quantity)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td className="text-end align-middle">
                    {(() => {
                      const gainLoss = computeOrderGainLoss(order, holdingsMap);
                      if (gainLoss === null) return '-';
                      return (
                        <span className={gainLoss > 0 ? 'text-success' : gainLoss < 0 ? 'text-danger' : ''}>
                          {gainLoss >= 0 ? '+' : '-'}${Math.abs(gainLoss).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="text-end align-middle" style={{ color: '#0d6efd', fontWeight: 'bold' }}>{order.size !== null && order.size !== undefined ? Number(order.size).toFixed(2) : '-'}</td>
                  <td className="align-middle">{order.created_at ? formatDateTime(order.created_at) : ''}</td>
                  <td className="align-middle">{order.order_date ? formatDate(order.order_date) : ''}</td>
                  <td className={`align-middle${order.status === 'COMMITTED' ? ' text-success' : ''}`}>{order.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        </div>
      </div>
      
      {/* Bottom pagination - show placeholder when loading */}
      {loading ? renderPaginationPlaceholder() : renderPagination()}
    </Container>
  );
}

export default Orders; 
